import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FlexiDate as DateComponent } from "../form/form";
import { handleMarkRefillFlexi } from "../../../services/reducers/pharmacy/dispense/investiGateRX";
import { showToast } from "../../../utils/helper/helper";
import { StockToComeMessage } from "../../../utils/helper/investigationRx";

const ARCHIVED_FLEXI_COPY_EVENT = 'archived-flexi-copy-to-all';

const isDateBefore = (dateValue, compareDateValue) => (
    typeof dateValue === 'string'
    && typeof compareDateValue === 'string'
    && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    && /^\d{4}-\d{2}-\d{2}$/.test(compareDateValue)
    && dateValue < compareDateValue
);

export const FlexiDelivery = ({ flexiDeliveryData = [], value, index, type = '', patientDetails, rxStockData, reef_date, currentDate, deliveryStatus }) => {

    const dispatch = useDispatch();
    const [formData, setFormData] = useState({});
    const [checkedIndexes, setCheckedIndexes] = useState({});
    const [showCopyAction, setShowCopyAction] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const flexiSaveQueueRef = useRef(Promise.resolve());

    const deliveryPrepData = {
        ...(value?.delivery_prep_data || {}),
        flexi_expiry_date: value?.delivery_prep_data?.flexi_expiry_date
            ?? value?.flexi_expiry_date
            ?? ''
    };
    const isPending = deliveryStatus === 'pending';
    const isDisabled = !isPending;
    const flexiMinDate = isDateBefore(reef_date, currentDate) ? currentDate : undefined;

    const resolveRowFormData = (incomingFormData, previousRowData = {}) => {
        const nextRowData = typeof incomingFormData === 'function'
            ? incomingFormData(previousRowData)
            : incomingFormData;

        return {
            ...previousRowData,
            ...(nextRowData || {})
        };
    };

    // Date pickers can fire consecutive updates while a Flexi item is first
    // being set up. Save them in order so an earlier request with an empty
    // expiry date cannot overwrite the user's later expiry selection.
    const queueFlexiSave = (payload) => {
        const save = flexiSaveQueueRef.current
            .catch(() => undefined)
            .then(() => dispatch(handleMarkRefillFlexi({
                requestType: 'markRefillFlexi',
                payload
            })));

        flexiSaveQueueRef.current = save;
        return save;
    };
    
    // Helper function to get date value, filtering out 0 and "0"
    const getDateValue = (index) => {
        const dateValue = formData[index]?.flexi_delivery_date || deliveryPrepData?.flexi_delivery_date;
        return (dateValue && dateValue !== '0' && dateValue !== 0) ? dateValue : '';
    };

    const getExpiryValue = (index) => {
        const expiryValue = formData[index]?.flexi_expiry_date || deliveryPrepData?.flexi_expiry_date;
        return (expiryValue && expiryValue !== '0' && expiryValue !== 0) ? expiryValue : '';
    };

    const isFlexiChecked = checkedIndexes[index] ?? Boolean(deliveryPrepData?.flexi_delivery);
    const eligibleArchivedPrescriptions = useMemo(() => (
        type === 'archived'
            ? flexiDeliveryData.filter((prescription) => (
                prescription?.status === 'pending'
                && prescription?.delivery_prep_data?.refill_id
            ))
            : []
    ), [flexiDeliveryData, type]);
    const archivedPatientGroupKey = useMemo(() => (
        eligibleArchivedPrescriptions
            .map((prescription) => prescription.delivery_prep_data.refill_id)
            .sort((a, b) => String(a).localeCompare(String(b)))
            .join('|')
    ), [eligibleArchivedPrescriptions]);

    // ✅ Auto-populate formData with delivery data dates on page refresh
    useEffect(() => {
        if (flexiDeliveryData?.length > 0) {
            const initialFormData = {};
            const initialCheckedIndexes = {};

            flexiDeliveryData?.forEach((value, index) => {
                const deliveryData = {
                    ...(value?.delivery_prep_data || {}),
                    flexi_expiry_date: value?.delivery_prep_data?.flexi_expiry_date
                        ?? value?.flexi_expiry_date
                        ?? ''
                };

                // If there's a flexi_delivery_date in delivery data, save it to formData
                if (deliveryData?.flexi_delivery_date || deliveryData?.flexi_expiry_date) {
                    initialFormData[index] = {
                        flexi_delivery_date: deliveryData.flexi_delivery_date || '',
                        flexi_expiry_date: deliveryData.flexi_expiry_date || ''
                    };
                }
                // If flexi_delivery is true, mark checkbox as checked
                if (deliveryData?.flexi_delivery) {
                    initialCheckedIndexes[index] = true;
                }
            });
            setFormData(initialFormData);
            setCheckedIndexes(initialCheckedIndexes);
        }
    }, [flexiDeliveryData]);

    useEffect(() => {
        if (type !== 'archived' || !archivedPatientGroupKey) return undefined;

        const syncCopiedFlexiValues = (event) => {
            if (event.detail?.groupKey !== archivedPatientGroupKey) return;

            const copiedDate = event.detail?.date || '';
            const nextFormData = {};
            const nextCheckedIndexes = {};

            const copiedRefillIds = new Set((event.detail?.refillIds || []).map(String));
            eligibleArchivedPrescriptions.forEach((prescription) => {
                if (!copiedRefillIds.has(String(prescription.delivery_prep_data.refill_id))) return;
                const prescriptionIndex = flexiDeliveryData.indexOf(prescription);
                nextFormData[prescriptionIndex] = {
                    ...(formData[prescriptionIndex] || {}),
                    flexi_delivery_date: copiedDate
                };
                nextCheckedIndexes[prescriptionIndex] = true;
            });

            setFormData((previous) => ({ ...previous, ...nextFormData }));
            setCheckedIndexes((previous) => ({ ...previous, ...nextCheckedIndexes }));
        };

        window.addEventListener(ARCHIVED_FLEXI_COPY_EVENT, syncCopiedFlexiValues);
        return () => window.removeEventListener(ARCHIVED_FLEXI_COPY_EVENT, syncCopiedFlexiValues);
    }, [archivedPatientGroupKey, eligibleArchivedPrescriptions, flexiDeliveryData, formData, type]);

    const handleCheckFlexiDelivery = (checked, index, refill_id) => {
        if (!refill_id) {
            showToast('Refill ID is missing...!', 'error');
            return;
        }
        // Update checked indexes state
        setCheckedIndexes(prev => ({
            ...prev,
            [index]: checked
        }));
        if (type === 'archived') {
            setShowCopyAction(checked);
        }

        // Clear the date when unchecked
        if (!checked) {
            setShowCopyAction(false);
            setFormData(prevData => ({
                ...prevData,
                [index]: { ...prevData[index], flexi_delivery_date: '' }
            }));
        }
        // Single API call with combined parameters
        const params = {
            refill_id: refill_id,
            flexi_delivery_date: checked ? '' : '', // Clear date when unchecked
            flexi_delivery: checked
        }

        queueFlexiSave(params);
    }

    const handleCopyFlexiToAll = async () => {
        const selectedDate = getDateValue(index);
        if (eligibleArchivedPrescriptions.length < 2 || isCopying) return;

        setIsCopying(true);
        try {
            const copyResults = await Promise.allSettled(eligibleArchivedPrescriptions.map((prescription) => (
                dispatch(handleMarkRefillFlexi({
                    requestType: 'markRefillFlexi',
                    rethrowError: true,
                    payload: {
                        refill_id: prescription.delivery_prep_data.refill_id,
                        flexi_delivery_date: selectedDate,
                        // Copy to all is a delivery-date action. Do not send an
                        // expiry value here: the list data may be older than a
                        // just-saved expiry selection, and an empty value would
                        // otherwise clear that expiry in the backend.
                        flexi_delivery: true
                    }
                }))
            )));

            const successfulRefillIds = eligibleArchivedPrescriptions
                .filter((_, prescriptionIndex) => copyResults[prescriptionIndex].status === 'fulfilled')
                .map((prescription) => prescription.delivery_prep_data.refill_id);

            if (successfulRefillIds.length === 0) {
                showToast('Unable to apply the Flexi date. Please try again.', 'error');
                return;
            }

            window.dispatchEvent(new CustomEvent(ARCHIVED_FLEXI_COPY_EVENT, {
                detail: {
                    groupKey: archivedPatientGroupKey,
                    date: selectedDate,
                    refillIds: successfulRefillIds
                }
            }));
            if (successfulRefillIds.length === eligibleArchivedPrescriptions.length) {
                setShowCopyAction(false);
                showToast('Flexi date applied to all pending RXs.', 'success');
            } else {
                showToast('Flexi date applied to some RXs. Please retry the remaining RXs.', 'error');
            }
        } finally {
            setIsCopying(false);
        }
    };

    return (<>

        {deliveryPrepData?.refill_id && <>
            {flexiDeliveryData?.investigate_dilaudid_delivery?.length === 0 ? null :
                <div className="flex items-start gap-2">
                    <input
                        type="checkbox"
                        checked={isFlexiChecked}
                        disabled={isDisabled}
                        className={`w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-0 focus:outline-none focus:ring-offset-0 checked:bg-blue-600 checked:border-blue-600 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onChange={(e) => {
                            if (!isDisabled) {
                                handleCheckFlexiDelivery(e.target.checked, index, deliveryPrepData?.refill_id);
                            }
                        }}
                    />

                    {(isFlexiChecked ? (
                        <div className="flex flex-col items-start gap-2">
                            <DateComponent
                                formData={formData[index] || {}}
                                setFormData={(incomingFormData) => {
                                    if (isDisabled) return;

                                    const previousRowData = formData[index] || {};
                                    const nextRowData = resolveRowFormData(incomingFormData, previousRowData);
                                    const date = nextRowData?.flexi_delivery_date || '';

                                    // Update form data for this specific index
                                    setFormData(prev => {
                                        const currentRowData = prev[index] || {};
                                        return {
                                            ...prev,
                                            [index]: resolveRowFormData(incomingFormData, currentRowData)
                                        };
                                    });

                                    // Handle both date setting and clearing
                                    if (date) {
                                        if (type === 'archived') setShowCopyAction(true);
                                        // Create params for API call
                                        const params = {
                                            refill_id: deliveryPrepData?.refill_id,
                                            flexi_delivery_date: date,
                                            flexi_expiry_date: nextRowData?.flexi_expiry_date || getExpiryValue(index),
                                            flexi_delivery: true
                                        }
                                        // Only update the checkbox state for this specific index
                                        setCheckedIndexes(prev => ({
                                            ...prev,
                                            [index]: true
                                        }));
                                        queueFlexiSave(params);
                                    } else {
                                        if (type === 'archived') setShowCopyAction(true);
                                        setCheckedIndexes(prev => ({
                                            ...prev,
                                            [index]: true
                                        }));
                                        // Handle date clearing (backspace/delete)
                                        const params = {
                                            refill_id: deliveryPrepData?.refill_id,
                                            flexi_delivery_date: '',
                                            flexi_expiry_date: nextRowData?.flexi_expiry_date || getExpiryValue(index),
                                            flexi_delivery: true
                                        }
                                        queueFlexiSave(params);
                                    }
                                }}
                                name='flexi_delivery_date'
                                dValue={getDateValue(index)}
                                label=''
                                placeholder='Date (optional)'
                                // type='removeToday'
                                type='flexiDeliveryDate'
                                minDate={flexiMinDate}
                                height='30px'
                                className={`!rounded !pl-2 !py-1.5 !w-[130px] ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                labelClassName='!ml-0 '
                                id={`flexi_delivery_date_${index}_${deliveryPrepData?.refill_id}`}
                                isDisabled={isDisabled}
                            />
                            <DateComponent
                                formData={formData[index] || {}}
                                setFormData={(incomingFormData) => {
                                    if (isDisabled) return;

                                    const previousRowData = formData[index] || {};
                                    const nextRowData = resolveRowFormData(incomingFormData, previousRowData);
                                    const expiryDate = nextRowData?.flexi_expiry_date || '';
                                    setFormData((previous) => ({
                                        ...previous,
                                        [index]: nextRowData
                                    }));
                                    queueFlexiSave({
                                        refill_id: deliveryPrepData?.refill_id,
                                        flexi_delivery_date: nextRowData?.flexi_delivery_date || getDateValue(index),
                                        flexi_expiry_date: expiryDate,
                                        flexi_delivery: true
                                    });
                                }}
                                name='flexi_expiry_date'
                                dValue={getExpiryValue(index)}
                                label=''
                                placeholder='Expiry Date'
                                type='flexiDeliveryDate'
                                height='30px'
                                className={`!rounded !pl-2 !py-1.5 !w-[130px] ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                labelClassName='!ml-0 '
                                id={`flexi_expiry_date_${index}_${deliveryPrepData?.refill_id}`}
                                isDisabled={isDisabled}
                            />
                            {type === 'archived' && showCopyAction && eligibleArchivedPrescriptions.length > 1 ? (
                                <button
                                    type="button"
                                    onClick={handleCopyFlexiToAll}
                                    disabled={isCopying}
                                    className="h-[30px] w-[130px] rounded bg-blue-600 px-2 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                                    title="Apply this Flexi date to all pending RXs for this patient"
                                >
                                    {isCopying ? 'Copying...' : 'Copy to all'}
                                </button>
                            ) : null}
                            {/* <span className="text-xs text-start">(optional)</span> */}
                        </div>
                    ) : null)}

                </div>
            }
        </>
        }

        {(index === 0 && reef_date === currentDate && checkedIndexes[index]) ? (
            <StockToComeMessage
                prescription_detail={patientDetails?.prescription_detail}
                rxStockData={rxStockData}
            />
        ) : null}


    </>
    )
}
